import React from 'react';
import { graphql } from 'gatsby';
//@ts-ignore
import * as styles from './blog-author-panel.module.css';
import CtaLink from '../Global/CtaLink';
import {
    KontentRichText,
    KontentStringValue,
    KontentMedia,
    CTAConfiguration,
} from '../../../types/KontentProps';
import RichText from '../Global/RichText';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import {
    optimizeKontentImageUrl,
    getDisplayDimensions,
    buildKontentSrcSet,
} from '../Global/DataUtils';

type AuthorProfile = {
    elements: {
        name: KontentStringValue;
        profile: KontentRichText;
        associated_link: any;
        headshot: KontentMedia;
    };
};
interface Props extends CTAConfiguration {
    kontentItemId?: string;
    untitled_linked_items: {
        value: AuthorProfile[];
    };
}

const BlogAuthorPanel: React.FC<Props> = ({
    kontentItemId,
    untitled_linked_items,
    ...props
}) => {
    const authorProfile = untitled_linked_items.value[0]?.elements;
    const cta =
        untitled_linked_items.value[0]?.elements.associated_link.value[0]
            ?.elements;
    const ctaAnchor = cta?.cta_location_anchor_point;
    const ctaWindowAction = cta?.cta_window_action.value[0]?.codename;
    const authImageFile =
        authorProfile?.headshot?.value[0]?.elements.file.value[0];
    const authImageUrl = authImageFile?.url;
    const anchorTarget = ctaWindowAction === 'same_window' ? '_self' : '_blank';
    const authorFullName = authorProfile?.name?.value;
    const noAuthorName = 'Sunlighten';

    const renderCta = () => {
        if (!cta) {
            return null;
        }

        return (
            <div className={styles.ctaContainer}>
                {cta?.cta_location && (
                    <CtaLink
                        href={BuildLinkHref(cta?.cta_location, ctaAnchor)}
                        target={anchorTarget}
                        visualStyle={'outlineLight'}
                        elementCodename="cta_label"
                    >
                        {cta?.cta_label?.value}
                    </CtaLink>
                )}
            </div>
        );
    };

    return (
        <section
            className={styles.wrapper}
            data-kontent-item-id={kontentItemId}
        >
            <div className={styles.contentContainer}>
                {authImageUrl ? (
                    <picture
                        className={styles.picture}
                        data-kontent-element-codename="headshot"
                    >
                        <img
                            src={optimizeKontentImageUrl(authImageUrl, {
                                w: 200,
                                fm: 'webp',
                                q: 80,
                            })}
                            srcSet={buildKontentSrcSet(authImageUrl, 200, {
                                fm: 'webp' as const,
                                q: 80,
                            })}
                            sizes="100px"
                            alt="profile picture"
                            {...getDisplayDimensions(
                                authImageFile?.width,
                                authImageFile?.height,
                                200
                            )}
                            loading="lazy"
                        />
                    </picture>
                ) : null}

                <div className={styles.contentBox}>
                    {authorFullName ? (
                        <p className={styles.authorFullName}>
                            WRITTEN BY: {authorFullName}
                        </p>
                    ) : (
                        <p className={styles.authorFullName}>{noAuthorName}</p>
                    )}

                    <div className={styles.authorShortBio}>
                        {RichText(
                            authorProfile?.profile,
                            false,
                            undefined,
                            undefined,
                            'profile'
                        )}
                    </div>

                    {renderCta()}
                </div>
            </div>
        </section>
    );
};

export default BlogAuthorPanel;

export const fragmentBlogAuthorPanel = graphql`
    fragment BlogAuthorPanel on kontent_item_component___author {
        elements {
            untitled_linked_items {
                value {
                    ... on kontent_item_data___author_profile {
                        id
                        elements {
                            name {
                                value
                            }
                            profile {
                                ...kontentRichText
                            }
                            headshot {
                                value {
                                    ... on kontent_item_media___image {
                                        id
                                        elements {
                                            caption {
                                                value
                                            }
                                            file {
                                                value {
                                                    url
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            associated_link {
                                value {
                                    ... on kontent_item_content_item___cta {
                                        id
                                        elements {
                                            cta_location {
                                                value {
                                                    ... on kontent_item_content_item___third_party_link {
                                                        elements {
                                                            third_party_url {
                                                                value
                                                            }
                                                        }
                                                    }
                                                    ... on kontent_item_layout___master_page {
                                                        id
                                                        elements {
                                                            slug {
                                                                value
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                            cta_location_anchor_point {
                                                value
                                            }
                                            cta_window_action {
                                                value {
                                                    codename
                                                }
                                            }
                                            cta_label {
                                                value
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;
